/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';

import {
  DateTimeRangePicker,
  DateRangePicker,
  MultiInputDateRangeField,
  MultiInputDateTimeRangeField,
} from '@mui/x-date-pickers-pro';
import dayjs, { type Dayjs } from 'dayjs';

import type { EventDateTime } from '../../../../../Data/types';
import { Field } from '../../../../shared/Field/Field';

type DateTimeRange = [Dayjs | null, Dayjs | null];

type Props = {
  label: string;
  startLabel?: string;
  endLabel?: string;
  isAllDay: boolean;
  isRecurring?: boolean;
  value: EventDateTime | null;
  onChange: (date: EventDateTime | null) => void;
  errorMessage?: string;
  tooltip?: string;
  required?: boolean;
  readOnly?: boolean;
};

export const DateTimePicker = ({
  label,
  startLabel,
  endLabel,
  isAllDay,
  value,
  onChange,
  errorMessage,
  tooltip,
  required = false,
  readOnly,
  isRecurring,
}: Props) => {
  const [dateTimeRange, setDateTimeRange] = useState<DateTimeRange>([
    value?.start ? dayjs(value.start) : null,
    value?.end ? dayjs(value.end) : null,
  ]);
  useEffect(() => {
    if (dateTimeRange[0] && dateTimeRange[1]) {
      // Reconstruct Dayjs objects using year/month/day/hour/minute to fix internal inconsistencies
      const start = dayjs()
        .year(dateTimeRange[0].year())
        .month(dateTimeRange[0].month())
        .date(dateTimeRange[0].date())
        .hour(dateTimeRange[0].hour())
        .minute(dateTimeRange[0].minute())
        .second(0)
        .millisecond(0);

      const end = dayjs()
        .year(dateTimeRange[1].year())
        .month(dateTimeRange[1].month())
        .date(dateTimeRange[1].date())
        .hour(dateTimeRange[1].hour())
        .minute(dateTimeRange[1].minute())
        .second(0)
        .millisecond(0);
      onChange({ start, end });
    }
  }, [dateTimeRange]);

  const handleRangeChange = (newValue: DateTimeRange) => {
    if (isAllDay) {
      setDateTimeRange([newValue[0]?.startOf('day') ?? null, newValue[1]?.endOf('day') ?? null]);
    } else {
      setDateTimeRange(newValue);
    }
  };

  const sharedProps = {
    localeText: { start: startLabel, end: endLabel },
    value: dateTimeRange,
    onChange: handleRangeChange,
    slotProps: {
      textField: {
        variant: 'outlined' as const,
        required: true,
        size: 'small' as const,
      },
      field: { readOnly: true },
    },
    readOnly,
  };

  return (
    <Field
      fullWidth
      required={required}
      label={label}
      tooltip={tooltip}
      validationMessage={errorMessage}
      validationState={errorMessage ? 'error' : 'none'}
      showValidationMessageIcon
    >
      {isAllDay || isRecurring ? (
        <DateRangePicker {...sharedProps} slots={{ field: MultiInputDateRangeField }} />
      ) : (
        <DateTimeRangePicker {...sharedProps} slots={{ field: MultiInputDateTimeRangeField }} />
      )}
    </Field>
  );
};
